import { useRef, useState } from "react";
import { motion, useDragControls } from "framer-motion";
import { toast } from "sonner";
import { LifeBuoy, X, Send, GripVertical, Loader2, Sparkles } from "lucide-react";
import api, { apiErr } from "@/lib/api";

const SUGGESTIONS = [
  "Como cadastro um funcionário?",
  "Como disparo uma campanha só pro comercial?",
  "Como assumo uma conversa no WhatsApp?",
  "Onde vejo o que a Isa não soube responder?",
  "Como ensino algo novo pra Isa?",
];

// minimal **bold** + line breaks
function Rich({ text }) {
  return (
    <div className="space-y-1.5">
      {(text || "").split("\n").map((line, i) =>
        line.trim() === "" ? <div key={i} className="h-1" /> : (
          <p key={i}>
            {line.split(/(\*\*[^*]+\*\*)/g).map((s, j) =>
              s.startsWith("**") && s.endsWith("**")
                ? <strong key={j} className="font-semibold text-zinc-900">{s.slice(2, -2)}</strong>
                : <span key={j}>{s}</span>)}
          </p>
        ))}
    </div>
  );
}

export default function SupportAssistant() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef((crypto.randomUUID && crypto.randomUUID()) || String(Date.now()));
  const constraintsRef = useRef(null);
  const controls = useDragControls();
  const endRef = useRef(null);

  const ask = async (question) => {
    const text = (question ?? q).trim();
    if (!text || busy) return;
    setMsgs((m) => [...m, { who: "me", text }]);
    setQ(""); setBusy(true);
    try {
      const { data } = await api.post("/support/ask", { question: text, session_id: sessionRef.current });
      setMsgs((m) => [...m, { who: "isa", text: data.answer }]);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 80); }
  };

  return (
    <div ref={constraintsRef} className="pointer-events-none fixed inset-0 z-[70]">
      <motion.div
        drag
        dragConstraints={constraintsRef}
        dragListener={!open}
        dragControls={controls}
        dragMomentum={false}
        dragElastic={0}
        onTap={() => { if (!open) setOpen(true); }}
        className="pointer-events-auto absolute bottom-6 right-6"
        data-testid="support-widget"
      >
        {open ? (
          <div className="flex h-[460px] w-[340px] flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-[0_12px_40px_rgb(0,0,0,0.16)] sm:w-[360px]">
            <div
              onPointerDown={(e) => controls.start(e)}
              className="flex cursor-move items-center gap-2 border-b border-zinc-200 bg-[#0b1220] px-4 py-3 text-white"
              data-testid="support-drag-handle"
            >
              <GripVertical size={15} className="text-zinc-400" />
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-white/10"><LifeBuoy size={14} /></span>
              <div className="flex-1 leading-tight">
                <p className="font-display text-sm font-semibold">Suporte</p>
                <p className="text-[10px] text-zinc-400">Dúvidas de como usar o sistema</p>
              </div>
              <button onClick={() => setOpen(false)} data-testid="support-close" className="rounded p-1 text-zinc-300 hover:bg-white/10 hover:text-white"><X size={16} /></button>
            </div>

            <div className="flex-1 space-y-3 overflow-y-auto bg-zinc-50 p-3 text-sm text-zinc-700" data-testid="support-messages">
              {!msgs.length && (
                <div className="space-y-3">
                  <div className="flex items-start gap-2 rounded-xl rounded-tl-sm border border-zinc-200 bg-white p-3">
                    <Sparkles size={15} className="mt-0.5 shrink-0 text-indigo-600" />
                    <p>Oi! Sou o suporte do sistema. Pergunte <b>onde fica</b> ou <b>como fazer</b> qualquer coisa aqui no console.</p>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} onClick={() => ask(s)} data-testid="support-suggestion"
                        className="rounded-full border border-zinc-200 bg-white px-2.5 py-1 text-xs text-zinc-500 transition-colors hover:border-indigo-300 hover:text-indigo-700">{s}</button>
                    ))}
                  </div>
                </div>
              )}
              {msgs.map((m, i) => (
                <div key={i} className={`flex ${m.who === "me" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3 py-2 ${m.who === "me" ? "rounded-br-sm bg-zinc-900 text-white" : "rounded-bl-sm border border-zinc-200 bg-white text-zinc-700"}`}>
                    {m.who === "me" ? m.text : <Rich text={m.text} />}
                  </div>
                </div>
              ))}
              {busy && <div className="flex items-center gap-2 text-xs text-zinc-400"><Loader2 size={13} className="animate-spin" /> pensando…</div>}
              <div ref={endRef} />
            </div>

            <div className="flex items-center gap-2 border-t border-zinc-200 bg-white p-2.5">
              <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
                placeholder="Como faço para…?" data-testid="support-input"
                className="flex-1 rounded-lg border border-input bg-white px-3 py-2 text-sm focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/15" />
              <button onClick={() => ask()} disabled={busy || !q.trim()} data-testid="support-send"
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-zinc-900 text-white transition hover:bg-zinc-800 disabled:opacity-40"><Send size={16} /></button>
            </div>
          </div>
        ) : (
          <button data-testid="support-fab"
            className="relative flex items-center gap-2 rounded-full bg-[#0b1220] py-3 pl-3 pr-4 text-sm font-medium text-white shadow-[0_10px_30px_rgb(11,18,32,0.28)] transition-transform hover:scale-105 active:scale-95">
            <span className="pointer-events-none absolute inset-0 rounded-full bg-[#0b1220] opacity-40 animate-ping" />
            <span className="relative flex items-center gap-2"><LifeBuoy size={18} /> Suporte</span>
          </button>
        )}
      </motion.div>
    </div>
  );
}
